import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, User, Camera, CreditCard, FileText, Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffPage = ({ user, token }) => {
  const [hubs, setHubs] = useState([]);
  const [selectedHub, setSelectedHub] = useState(user?.hub_id || "");
  const [staffList, setStaffList] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteStaffId, setDeleteStaffId] = useState(null);
  const [viewStaff, setViewStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    place: "",
    age: "",
    address: "",
    aadhar_number: "",
    pan_number: "",
    aadhar_front_photo: "",
    aadhar_back_photo: "",
    pan_photo: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchHubs();
    fetchStaffList();
  }, []);

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHubs(response.data);
      if (response.data.length > 0 && !user?.hub_id) {
        setSelectedHub(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/staff`, {
        headers: { Authorization: `Bearer ${token}` },
        params: user?.hub_id ? { hub_id: user.hub_id } : {}
      });
      setStaffList(response.data);
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const handleImageUpload = (field) => (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, [field]: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API}/staff`,
        {
          hub_id: selectedHub,
          name: formData.name,
          place: formData.place,
          age: parseInt(formData.age),
          address: formData.address,
          aadhar_number: formData.aadhar_number,
          pan_number: formData.pan_number || null,
          aadhar_front_photo: formData.aadhar_front_photo || null,
          aadhar_back_photo: formData.aadhar_back_photo || null,
          pan_photo: formData.pan_photo || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(user?.role === 'admin' ? "Staff member added!" : "Staff data submitted for approval!");
      setFormData({ name: "", place: "", age: "", address: "", aadhar_number: "", pan_number: "", aadhar_front_photo: "", aadhar_back_photo: "", pan_photo: "" });
      setIsDialogOpen(false);
      fetchStaffList();
    } catch (error) {
      if (error.response?.status === 202) {
        toast.success("Staff data submitted for admin approval!");
        setFormData({ name: "", place: "", age: "", address: "", aadhar_number: "", pan_number: "", aadhar_front_photo: "", aadhar_back_photo: "", pan_photo: "" });
        setIsDialogOpen(false);
      } else {
        toast.error(error.response?.data?.detail || "Failed to add staff");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/staff/${deleteStaffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Staff member deleted!");
      setDeleteStaffId(null);
      fetchStaffList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const canManageStaff = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'staff';

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading tracking-tight">Staff</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">Manage team members</p>
        </div>
        {canManageStaff && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 w-full sm:w-auto" data-testid="add-staff-button">
                <UserPlus className="w-4 h-4" />
                Add Staff
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mx-4">
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl font-heading">Add Staff Member</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3">
                {!user?.hub_id && (
                  <div className="space-y-2">
                    <Label htmlFor="hub" className="text-sm">Hub</Label>
                    <Select value={selectedHub} onValueChange={setSelectedHub}>
                      <SelectTrigger data-testid="staff-hub-select">
                        <SelectValue placeholder="Select Hub" />
                      </SelectTrigger>
                      <SelectContent>
                        {hubs.map((hub) => (
                          <SelectItem key={hub.id} value={hub.id}>
                            {hub.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm">Full Name</Label>
                  <Input
                    id="name"
                    data-testid="staff-name-input"
                    type="text"
                    placeholder="Enter name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-9"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="age" className="text-sm">Age</Label>
                    <Input
                      id="age"
                      data-testid="staff-age-input"
                      type="number"
                      placeholder="Age"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      className="h-9"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="place" className="text-sm">Place</Label>
                    <Input
                      id="place"
                      data-testid="staff-place-input"
                      type="text"
                      placeholder="Place"
                      value={formData.place}
                      onChange={(e) => setFormData({ ...formData, place: e.target.value })}
                      className="h-9"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-sm">Address</Label>
                  <Input
                    id="address"
                    data-testid="staff-address-input"
                    type="text"
                    placeholder="Enter address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="h-9"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="aadhar_number" className="text-sm">Aadhar Number</Label>
                    <Input
                      id="aadhar_number"
                      data-testid="staff-aadhar-input"
                      type="text"
                      placeholder="12-digit Aadhar"
                      value={formData.aadhar_number}
                      onChange={(e) => setFormData({ ...formData, aadhar_number: e.target.value })}
                      className="h-9"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pan_number" className="text-sm">PAN Number (Optional)</Label>
                    <Input
                      id="pan_number"
                      data-testid="staff-pan-input"
                      type="text"
                      placeholder="e.g., ABCDE1234F"
                      value={formData.pan_number}
                      onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })}
                      className="h-9"
                    />
                  </div>
                </div>

                {/* Document Photos */}
                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-sm font-semibold">Document Photos</Label>
                  
                  <div className="grid grid-cols-3 gap-2">
                    {/* Aadhar Front */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Aadhar Front</Label>
                      <Input
                        id="aadhar_front"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload('aadhar_front_photo')}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('aadhar_front').click()}
                        className="w-full h-16 flex flex-col gap-1 text-xs"
                      >
                        {formData.aadhar_front_photo ? (
                          <img src={formData.aadhar_front_photo} alt="Aadhar Front" className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5" />
                            <span>Front</span>
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Aadhar Back */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Aadhar Back</Label>
                      <Input
                        id="aadhar_back"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload('aadhar_back_photo')}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('aadhar_back').click()}
                        className="w-full h-16 flex flex-col gap-1 text-xs"
                      >
                        {formData.aadhar_back_photo ? (
                          <img src={formData.aadhar_back_photo} alt="Aadhar Back" className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5" />
                            <span>Back</span>
                          </>
                        )}
                      </Button>
                    </div>

                    {/* PAN */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">PAN Card</Label>
                      <Input
                        id="pan_photo"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload('pan_photo')}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('pan_photo').click()}
                        className="w-full h-16 flex flex-col gap-1 text-xs"
                      >
                        {formData.pan_photo ? (
                          <img src={formData.pan_photo} alt="PAN" className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <>
                            <FileText className="w-5 h-5" />
                            <span>PAN</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                <Button
                  data-testid="staff-submit-button"
                  type="submit"
                  className="w-full h-9"
                  disabled={loading}
                >
                  {loading ? "Adding..." : (user?.role === 'admin' ? "Add Staff" : "Submit for Approval")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6" data-testid="staff-grid">
        {staffList.length === 0 ? (
          <Card className="col-span-full shadow-lg border-border/50">
            <CardContent className="flex flex-col items-center justify-center p-8 sm:p-12 text-muted-foreground">
              <User className="w-12 h-12 sm:w-16 sm:h-16 mb-3 sm:mb-4" />
              <p className="text-sm sm:text-lg">No staff members yet</p>
            </CardContent>
          </Card>
        ) : (
          staffList.map((staff) => (
            <Card key={staff.id} className="shadow-lg border-border/50 hover:shadow-xl transition-shadow duration-300" data-testid={`staff-card-${staff.id}`}>
              <CardHeader className="pb-2 sm:pb-3 p-3 sm:p-4 lg:p-6">
                <div className="flex justify-center mb-2 sm:mb-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center border-4 border-primary/20">
                    <User className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-primary" />
                  </div>
                </div>
                <CardTitle className="text-center text-base sm:text-lg lg:text-xl font-heading">{staff.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 sm:space-y-2 text-xs sm:text-sm p-3 sm:p-4 lg:p-6 pt-0">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Age:</span>
                  <span className="font-medium">{staff.age}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Place:</span>
                  <span className="font-medium">{staff.place}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-muted-foreground mb-1">Aadhar:</span>
                  <span className="font-medium font-mono text-[10px] sm:text-xs">{staff.aadhar_number}</span>
                </div>
                {staff.pan_number && (
                  <div className="flex flex-col">
                    <span className="text-muted-foreground mb-1">PAN:</span>
                    <span className="font-medium font-mono text-[10px] sm:text-xs">{staff.pan_number}</span>
                  </div>
                )}
                
                {/* Document indicators */}
                <div className="flex gap-1 pt-2">
                  {staff.aadhar_front_photo && (
                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[9px] rounded">Aadhar F</span>
                  )}
                  {staff.aadhar_back_photo && (
                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[9px] rounded">Aadhar B</span>
                  )}
                  {staff.pan_photo && (
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[9px] rounded">PAN</span>
                  )}
                </div>

                <div className="flex gap-2 pt-3 sm:pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1 h-8 text-xs"
                    onClick={() => setViewStaff(staff)}
                    data-testid={`view-staff-${staff.id}`}
                  >
                    <Eye className="w-3 h-3" />
                    View
                  </Button>
                  {(user?.role === 'admin' || user?.role === 'manager') && (
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex-1 gap-1 h-8 text-xs"
                      onClick={() => setDeleteStaffId(staff.id)}
                      data-testid={`delete-staff-${staff.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* View Staff Dialog */}
      <Dialog open={!!viewStaff} onOpenChange={() => setViewStaff(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-heading">{viewStaff?.name}</DialogTitle>
          </DialogHeader>
          {viewStaff && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Age:</span>
                  <p className="font-medium">{viewStaff.age}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Place:</span>
                  <p className="font-medium">{viewStaff.place}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Address:</span>
                  <p className="font-medium">{viewStaff.address}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Aadhar:</span>
                  <p className="font-medium font-mono">{viewStaff.aadhar_number}</p>
                </div>
                {viewStaff.pan_number && (
                  <div>
                    <span className="text-muted-foreground">PAN:</span>
                    <p className="font-medium font-mono">{viewStaff.pan_number}</p>
                  </div>
                )}
              </div>

              {/* Document Photos */}
              <div className="space-y-3 pt-2 border-t">
                <Label className="text-sm font-semibold">Documents</Label>
                <div className="grid grid-cols-3 gap-2">
                  {viewStaff.aadhar_front_photo ? (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Aadhar Front</Label>
                      <img src={viewStaff.aadhar_front_photo} alt="Aadhar Front" className="w-full h-24 object-cover rounded border" />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Aadhar Front</Label>
                      <div className="w-full h-24 bg-muted rounded border flex items-center justify-center text-muted-foreground text-xs">Not uploaded</div>
                    </div>
                  )}
                  {viewStaff.aadhar_back_photo ? (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Aadhar Back</Label>
                      <img src={viewStaff.aadhar_back_photo} alt="Aadhar Back" className="w-full h-24 object-cover rounded border" />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Aadhar Back</Label>
                      <div className="w-full h-24 bg-muted rounded border flex items-center justify-center text-muted-foreground text-xs">Not uploaded</div>
                    </div>
                  )}
                  {viewStaff.pan_photo ? (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">PAN Card</Label>
                      <img src={viewStaff.pan_photo} alt="PAN" className="w-full h-24 object-cover rounded border" />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">PAN Card</Label>
                      <div className="w-full h-24 bg-muted rounded border flex items-center justify-center text-muted-foreground text-xs">Not uploaded</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteStaffId} onOpenChange={() => setDeleteStaffId(null)}>
        <AlertDialogContent className="mx-4 max-w-sm sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              This will permanently delete the staff member.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffPage;
